import { useState, useMemo, useEffect } from 'react';
import Layout from '@/components/feature/Layout';
import {
  fetchNotifications,
  markNotificationRead,
  markAllRead,
  fetchAuditLogs,
  type NotificationItem,
  type AuditLogItem,
} from '@/api/notifications';

type FilterTab = 'all' | 'unread' | 'system' | 'operation' | 'dingtalk' | 'feishu';
type ViewMode = 'notifications' | 'logs';

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [notificationsError, setNotificationsError] = useState<string | null>(null);

  const [logs, setLogs] = useState<AuditLogItem[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsLoaded, setLogsLoaded] = useState(false);

  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('notifications');

  useEffect(() => {
    let cancelled = false;
    setNotificationsLoading(true);
    setNotificationsError(null);
    fetchNotifications()
      .then((data) => {
        if (!cancelled) setNotifications(data);
      })
      .catch(() => {
        if (!cancelled) setNotificationsError('通知加载失败，请稍后重试');
      })
      .finally(() => {
        if (!cancelled) setNotificationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (viewMode !== 'logs' || logsLoaded) return;
    let cancelled = false;
    setLogsLoading(true);
    setLogsError(null);
    fetchAuditLogs()
      .then((data) => {
        if (!cancelled) {
          setLogs(data);
          setLogsLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLogsError('操作日志加载失败，请稍后重试');
      })
      .finally(() => {
        if (!cancelled) setLogsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, logsLoaded]);

  const filteredNotifications = useMemo(() => {
    let result = notifications;
    switch (activeFilter) {
      case 'unread':
        result = result.filter((n) => !n.read);
        break;
      case 'system':
        result = result.filter((n) => n.type === 'system');
        break;
      case 'operation':
        result = result.filter((n) => n.type === 'operation');
        break;
      case 'dingtalk':
        result = result.filter((n) => n.type === 'dingtalk');
        break;
      case 'feishu':
        result = result.filter((n) => n.type === 'feishu');
        break;
    }
    return result;
  }, [notifications, activeFilter]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const markAsRead = async (id: string) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );
    try {
      await markNotificationRead(id);
    } catch {
      // Revert on failure
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: false } : n)),
      );
    }
  };

  const markAllAsRead = async () => {
    const previous = notifications;
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await markAllRead();
    } catch {
      setNotifications(previous);
    }
  };

  const getTypeConfig = (type: NotificationItem['type']) => {
    switch (type) {
      case 'dingtalk':
        return { icon: 'ri-dingding-line', bg: 'bg-[#0089FF]/10', text: 'text-[#0089FF]', label: '钉钉' };
      case 'feishu':
        return { icon: 'ri-rocket-line', bg: 'bg-[#3370FF]/10', text: 'text-[#3370FF]', label: '飞书' };
      case 'system':
        return { icon: 'ri-settings-3-line', bg: 'bg-primary-100', text: 'text-primary-600', label: '系统' };
      case 'operation':
        return { icon: 'ri-file-list-3-line', bg: 'bg-accent-100', text: 'text-accent-600', label: '操作' };
    }
  };

  const getLogIcon = (action: string) => {
    if (action.includes('密码')) return 'ri-key-2-line';
    if (action.includes('飞书')) return 'ri-rocket-line';
    if (action.includes('导入')) return 'ri-file-upload-line';
    if (action.includes('标签')) return 'ri-price-tag-3-line';
    if (action.includes('新增') || action.includes('添加')) return 'ri-add-circle-line';
    if (action.includes('删除')) return 'ri-delete-bin-line';
    if (action.includes('编辑') || action.includes('修改')) return 'ri-edit-line';
    if (action.includes('登录')) return 'ri-login-circle-line';
    if (action.includes('导出')) return 'ri-download-line';
    return 'ri-information-line';
  };

  const filterTabs: { key: FilterTab; label: string; icon: string }[] = [
    { key: 'all', label: '全部', icon: 'ri-notification-3-line' },
    { key: 'unread', label: '未读', icon: 'ri-mail-unread-line' },
    { key: 'system', label: '系统', icon: 'ri-settings-3-line' },
    { key: 'operation', label: '操作', icon: 'ri-file-list-3-line' },
    { key: 'dingtalk', label: '钉钉', icon: 'ri-dingding-line' },
    { key: 'feishu', label: '飞书', icon: 'ri-rocket-line' },
  ];

  return (
    <Layout>
      <div className="p-4 md:p-6 space-y-5 max-w-[1100px] mx-auto">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">通知中心</h1>
            <p className="text-sm text-foreground-500 mt-1">查看系统通知、操作日志和平台绑定消息</p>
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'notifications' && unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-accent-600 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-double-line text-sm"></i>
                全部已读
              </button>
            )}
            <div className="flex items-center bg-background-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode('notifications')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'notifications'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-notification-3-line mr-1"></i>
                通知
                {unreadCount > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-primary-500 text-background-50 rounded-full text-[10px] font-bold">
                    {unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setViewMode('logs')}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                  viewMode === 'logs'
                    ? 'bg-background-50 text-foreground-900'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                <i className="ri-history-line mr-1"></i>
                操作日志
              </button>
            </div>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {filterTabs.slice(0, 6).map((tab) => {
            const count =
              tab.key === 'all'
                ? notifications.length
                : tab.key === 'unread'
                  ? unreadCount
                  : notifications.filter((n) => n.type === tab.key).length;
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveFilter(tab.key);
                  setViewMode('notifications');
                }}
                className={`bg-background-50 rounded-xl p-4 border text-left transition-all cursor-pointer ${
                  activeFilter === tab.key && viewMode === 'notifications'
                    ? 'border-primary-400 ring-2 ring-primary-100'
                    : 'border-background-200/70 hover:border-background-300'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    tab.key === 'dingtalk' ? 'bg-[#0089FF]/10' :
                    tab.key === 'feishu' ? 'bg-[#3370FF]/10' :
                    tab.key === 'system' ? 'bg-primary-100' :
                    tab.key === 'operation' ? 'bg-accent-100' :
                    tab.key === 'unread' ? 'bg-red-50' :
                    'bg-background-100'
                  }`}>
                    <i className={`${tab.icon} text-sm ${
                      tab.key === 'dingtalk' ? 'text-[#0089FF]' :
                      tab.key === 'feishu' ? 'text-[#3370FF]' :
                      tab.key === 'system' ? 'text-primary-600' :
                      tab.key === 'operation' ? 'text-accent-600' :
                      tab.key === 'unread' ? 'text-red-500' :
                      'text-foreground-600'
                    }`}></i>
                  </div>
                  <div>
                    <p className="text-lg font-heading font-bold text-foreground-900">{count}</p>
                    <p className="text-[11px] text-foreground-500">{tab.label}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Notifications list */}
        {viewMode === 'notifications' && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            {notificationsLoading ? (
              <div className="px-4 py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
                  <p className="text-sm text-foreground-500">加载中...</p>
                </div>
              </div>
            ) : notificationsError ? (
              <div className="px-4 py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                    <i className="ri-error-warning-line text-2xl text-red-500"></i>
                  </div>
                  <p className="text-sm text-foreground-500">{notificationsError}</p>
                </div>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-notification-off-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">暂无{activeFilter === 'unread' ? '未读' : ''}通知</p>
                </div>
              </div>
            ) : (
              <div className="divide-y divide-background-200/70">
                {filteredNotifications.map((notification) => {
                  const config = getTypeConfig(notification.type);
                  return (
                    <div
                      key={notification.id}
                      onClick={() => !notification.read && markAsRead(notification.id)}
                      className={`flex items-start gap-4 px-5 md:px-6 py-4 transition-colors cursor-pointer ${
                        notification.read
                          ? 'hover:bg-background-50/80'
                          : 'bg-primary-50/30 hover:bg-primary-50/50'
                      }`}
                    >
                      {/* Unread dot */}
                      <div className="flex-shrink-0 pt-1">
                        {!notification.read ? (
                          <span className="w-2.5 h-2.5 rounded-full bg-primary-500 block mt-1"></span>
                        ) : (
                          <div className="w-2.5 h-2.5 mt-1" />
                        )}
                      </div>

                      {/* Type icon */}
                      <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <i className={`${config.icon} text-lg ${config.text}`}></i>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className={`text-sm font-medium ${notification.read ? 'text-foreground-700' : 'text-foreground-900 font-semibold'}`}>
                            {notification.title}
                          </h3>
                          <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${config.bg} ${config.text}`}>
                            {config.label}
                          </span>
                        </div>
                        <p className={`text-sm mt-1 leading-relaxed ${notification.read ? 'text-foreground-500' : 'text-foreground-600'}`}>
                          {notification.description}
                        </p>
                        <p className="text-xs text-foreground-400 mt-2">{notification.time}</p>
                      </div>

                      {/* Mark read button */}
                      {!notification.read && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(notification.id);
                          }}
                          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
                          title="标为已读"
                        >
                          <i className="ri-check-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Operation logs */}
        {viewMode === 'logs' && (
          <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            {logsLoading ? (
              <div className="px-4 py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
                  <p className="text-sm text-foreground-500">加载中...</p>
                </div>
              </div>
            ) : logsError ? (
              <div className="px-4 py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
                    <i className="ri-error-warning-line text-2xl text-red-500"></i>
                  </div>
                  <p className="text-sm text-foreground-500">{logsError}</p>
                </div>
              </div>
            ) : logs.length === 0 ? (
              <div className="px-4 py-16 text-center">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-history-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">暂无操作日志</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left px-5 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">操作类型</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">详情</th>
                      <th className="text-left px-5 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider hidden md:table-cell whitespace-nowrap">操作人</th>
                      <th className="text-right px-5 py-3 text-xs font-medium text-foreground-500 uppercase tracking-wider whitespace-nowrap">时间</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/70">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-background-100/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                              <i className={`${getLogIcon(log.action)} text-secondary-600 text-sm`}></i>
                            </div>
                            <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{log.action}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-sm text-foreground-600 max-w-[400px]">{log.detail}</p>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <span className="text-sm text-foreground-600 whitespace-nowrap">{log.operator}</span>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span className="text-xs text-foreground-500 whitespace-nowrap">{log.time}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
